import { readEffectiveListingMinPrice } from "./hotelDisplayPrice";

const normalizeHotelListingName = (name) =>
  String(name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");

const normalizeHotelCodeKey = (code) => {
  const raw = String(code ?? "").trim().toLowerCase();
  if (!raw) return "";
  if (raw.startsWith("hsid")) {
    const tail = raw.split("-").pop() ?? raw;
    return tail.replace(/^hsid/i, "");
  }
  return raw;
};

const hotelCodesReferToSameProperty = (a, b) => {
  const sa = String(a ?? "").trim();
  const sb = String(b ?? "").trim();
  if (!sa || !sb) return false;
  if (sa === sb) return true;
  const na = normalizeHotelCodeKey(sa);
  const nb = normalizeHotelCodeKey(sb);
  if (na && nb && na === nb) return true;
  if (na && sb.toLowerCase().includes(na)) return true;
  if (nb && sa.toLowerCase().includes(nb)) return true;
  return false;
};

const listingCityKey = (hotel) =>
  normalizeHotelListingName(
    hotel?.addresses?.[0]?.cityName ??
      hotel?.addresses?.[0]?.CityName ??
      hotel?.city ??
      "",
  );

const readListingPrice = (hotel) => readEffectiveListingMinPrice(hotel);

export const readHotelListingSortPrice = (hotel) => {
  const price = readListingPrice(hotel);
  if (price > 0 && hotel?.isPriceAvailable !== false) {
    return price;
  }
  return Number.POSITIVE_INFINITY;
};

/** Rows that came back with live supplier fare — safe to render on listing cards. */
export const extractPricedListingHotels = (hotels = []) =>
  (Array.isArray(hotels) ? hotels : []).filter((hotel) => {
    const price = readListingPrice(hotel);
    return price > 0 && hotel?.isPriceAvailable !== false;
  });

/** Skip broken/relative listing URLs (e.g. "1.jpg" → ORB / 307 HTML). */
export const isUsableHotelListingImageUrl = (url) => {
  const raw = String(url ?? "").trim();
  if (raw.length < 12) return false;
  if (!/^https?:\/\//i.test(raw)) return false;
  if (/no_img|placeholder|HotelNA\.jpg|click.?here|more.?details|best.?prices|book.?now/i.test(raw)) return false;
  let parsed;
  try {
    parsed = new URL(raw);
  } catch {
    return false;
  }
  if (!parsed.hostname || !parsed.hostname.includes(".")) return false;
  const path = parsed.pathname.toLowerCase();
  const file = path.split("/").pop() ?? "";
  // Generic index filenames often redirect to HTML — allow known hotel CDNs (TBO/xmlhub static).
  if (/^(1|2|3|main|thumb)\.(jpg|jpeg|png|webp)$/i.test(file)) {
    if (!TRUSTED_HOTEL_IMAGE_HOST.test(parsed.hostname)) {
      return false;
    }
  }
  return true;
};

const TRUSTED_HOTEL_IMAGE_HOST =
  /tripjack|hms-in-media|cloudfront|akamai|tbo|grn|xmlhub|hotelbeds|hbcdn|travelboutique|photos\.|static\.travel|cdn\.|media\.|images\./i;

/** Prefer CDN URLs that load reliably as the card hero image. */
export const sortHotelListingImageUrls = (urls = []) => {
  const preferred = [];
  const other = [];
  urls.forEach((url) => {
    if (TRUSTED_HOTEL_IMAGE_HOST.test(String(url))) {
      preferred.push(url);
    } else {
      other.push(url);
    }
  });
  return [...preferred, ...other];
};

/** Collect image URLs from all common hotel listing shapes. */
export const collectHotelListingImageUrls = (hotel = {}) => {
  const raw = [
    ...(Array.isArray(hotel?.images) ? hotel.images : []),
    ...(Array.isArray(hotel?.Images) ? hotel.Images : []),
    ...(Array.isArray(hotel?.imageList) ? hotel.imageList : []),
    ...(Array.isArray(hotel?.ImageList) ? hotel.ImageList : []),
    ...(Array.isArray(hotel?.propertyImages) ? hotel.propertyImages : []),
    ...(Array.isArray(hotel?.hotelImages) ? hotel.hotelImages : []),
    ...(Array.isArray(hotel?.img) ? hotel.img : []),
    ...(Array.isArray(hotel?.imgs) ? hotel.imgs : []),
  ];
  if (hotel?.images && !Array.isArray(hotel.images) && typeof hotel.images === "object") {
    raw.push(
      hotel.images.main,
      hotel.images.thumb,
      hotel.images.thumbnail,
      hotel.images.url,
      hotel.images.xl,
      hotel.images.l,
      hotel.images.m,
    );
  }
  if (typeof hotel?.img === "string") {
    raw.push(hotel.img);
  }
  const urls = [];
  const seen = new Set();
  for (const item of raw) {
    const url = String(
      typeof item === "string"
        ? item
        : item?.url ??
            item?.Url ??
            item?.tns ??
            item?.Tns ??
            item?.main ??
            item?.thumb ??
            item?.thumbnail ??
            item?.src ??
            item?.path ??
            item?.href ??
            item?.xl ??
            item?.l ??
            item?.m ??
            item?.s ??
            item?.links?.Standard?.href ??
            item?.links?.standard?.href ??
            item?.links?.XL?.href ??
            item?.links?.large?.href ??
            "",
    ).trim();
    if (!url || seen.has(url)) continue;
    if (!isUsableHotelListingImageUrl(url)) continue;
    seen.add(url);
    urls.push(url);
  }
  return sortHotelListingImageUrls(urls);
};

/** Merge images from one or more listing rows (static + live search). */
export const mergeHotelListingImages = (...hotels) => {
  const urls = [];
  const seen = new Set();
  hotels.forEach((hotel) => {
    if (!hotel || typeof hotel !== "object") return;
    collectHotelListingImageUrls(hotel).forEach((url) => {
      if (seen.has(url)) return;
      seen.add(url);
      urls.push(url);
    });
  });
  return urls;
};

/** Enrich a filtered/sorted row with best metadata from the full inventory. */
export const enrichHotelListingRow = (hotel, inventory = []) => {
  if (!hotel || typeof hotel !== "object") return hotel;
  const siblings = (Array.isArray(inventory) ? inventory : []).filter((row) =>
    hotelsReferToSameListing(row, hotel),
  );
  const mergedImages = mergeHotelListingImages(hotel, ...siblings);
  if (siblings.length === 0) {
    return mergedImages.length
      ? { ...hotel, images: mergedImages, Images: mergedImages }
      : hotel;
  }
  const preferred = siblings.reduce((best, row) =>
    pickPreferredListingEntry(best, row),
  );
  const pricedSiblings = siblings.filter(
    (row) => readListingPrice(row) > 0 && row?.isPriceAvailable !== false,
  );
  const pickListingPriceSource = (best, row) => {
    const portalBest = best?.portalPriceApplied === true && readListingPrice(best) > 0;
    const portalRow = row?.portalPriceApplied === true && readListingPrice(row) > 0;
    if (portalRow && !portalBest) {
      return row;
    }
    if (portalBest && !portalRow) {
      return best;
    }
    return readListingPrice(row) < readListingPrice(best) ? row : best;
  };
  const priceSource =
    pricedSiblings.length > 0
      ? pricedSiblings.reduce(pickListingPriceSource)
      : readListingPrice(hotel) > 0
        ? hotel
        : preferred;
  const listingImages =
    mergedImages.length > 0
      ? mergedImages
      : collectHotelListingImageUrls(preferred);
  return {
    ...preferred,
    ...hotel,
    images: listingImages,
    Images: listingImages,
    imageList: listingImages,
    ImageList: listingImages,
    hotelMinPrice: readListingPrice(priceSource),
    HotelMinPrice: readListingPrice(priceSource),
    hotelPublishPrice:
      priceSource?.hotelPublishPrice ?? priceSource?.HotelPublishPrice,
    hotelNetPrice: priceSource?.hotelNetPrice ?? priceSource?.HotelNetPrice,
    supplierApiFare: priceSource?.supplierApiFare ?? priceSource?.supplierGrossPrice,
    supplierGrossPrice: priceSource?.supplierGrossPrice ?? priceSource?.supplierApiFare,
    supplierListPrice: priceSource?.supplierListPrice ?? priceSource?.supplierApiFare,
    portalPriceApplied: priceSource?.portalPriceApplied,
    portalDiscountRate: priceSource?.portalDiscountRate,
    portalMarkupRate: priceSource?.portalMarkupRate,
    isPriceAvailable:
      priceSource?.isPriceAvailable !== false && readListingPrice(priceSource) > 0,
    starRating:
      pickListingStarRating(hotel, preferred, ...siblings) ||
      hotel?.starRating ||
      preferred?.starRating ||
      "",
    StarRating:
      pickListingStarRating(hotel, preferred, ...siblings) ||
      hotel?.StarRating ||
      preferred?.StarRating ||
      "",
  };
};

export const pickListingStarRating = (...hotels) => {
  for (const hotel of hotels) {
    if (!hotel || typeof hotel !== "object") continue;
    const raw =
      hotel.starRating ??
      hotel.StarRating ??
      hotel.star_rating ??
      hotel.rt ??
      hotel.stars ??
      hotel.star;
    const num = Number(raw);
    if (Number.isFinite(num) && num > 0 && num <= 5) {
      return String(Math.round(num));
    }
  }
  return "";
};

export const hasListingImages = (hotel) => {
  return collectHotelListingImageUrls(hotel).length > 0;
};

const rankListingEntry = (hotel) => {
  let score = 0;
  if (hasListingImages(hotel)) score += 100000;
  const code = String(hotel?.hotelCode ?? hotel?.HotelCode ?? "")
    .trim()
    .toLowerCase();
  if (code.startsWith("hsid")) score += 50000;
  if (hotel?.isPriceAvailable !== false) score += 10000;
  const price = readListingPrice(hotel);
  if (price > 0) score += 5000 - Math.min(Math.floor(price / 50000), 4000);
  const addressLine = String(
    hotel?.addresses?.[0]?.addressLine1 ??
      hotel?.addresses?.[0]?.AddressLine1 ??
      "",
  ).trim();
  if (
    addressLine.length > 20 &&
    !/^dubai,?\s*united arab emirates$/i.test(addressLine)
  ) {
    score += 500;
  }
  return score;
};

export const hotelsReferToSameListing = (a, b) => {
  if (!a || !b) return false;
  const codeA =
    a?.hotelCode ?? a?.HotelCode ?? a?.supplierHotelCode ?? a?.SupplierHotelCode;
  const codeB =
    b?.hotelCode ?? b?.HotelCode ?? b?.supplierHotelCode ?? b?.SupplierHotelCode;
  if (hotelCodesReferToSameProperty(codeA, codeB)) return true;
  const nameA = normalizeHotelListingName(a?.hotelName ?? a?.HotelName);
  const nameB = normalizeHotelListingName(b?.hotelName ?? b?.HotelName);
  if (!nameA || !nameB || nameA !== nameB) return false;
  const cityA = listingCityKey(a);
  const cityB = listingCityKey(b);
  if (cityA && cityB && cityA !== cityB) return false;
  return true;
};

const pickPreferredListingEntry = (a, b) => {
  const priceA = readListingPrice(a);
  const priceB = readListingPrice(b);
  const pricedA = priceA > 0 && a?.isPriceAvailable !== false;
  const pricedB = priceB > 0 && b?.isPriceAvailable !== false;
  const portalA = a?.portalPriceApplied === true && priceA > 0;
  const portalB = b?.portalPriceApplied === true && priceB > 0;
  if (portalA && !portalB) {
    return a;
  }
  if (portalB && !portalA) {
    return b;
  }
  if (pricedA && pricedB && priceA !== priceB) {
    return priceA <= priceB ? a : b;
  }
  return rankListingEntry(a) >= rankListingEntry(b) ? a : b;
};

export const dedupeHotelListingResults = (hotels = []) => {
  if (!Array.isArray(hotels) || hotels.length <= 1) {
    return Array.isArray(hotels) ? hotels : [];
  }
  const result = [];
  hotels.forEach((hotel) => {
    if (!hotel || typeof hotel !== "object") return;
    const dupIndex = result.findIndex((existing) =>
      hotelsReferToSameListing(existing, hotel),
    );
    if (dupIndex < 0) {
      result.push(hotel);
      return;
    }
    const merged = pickPreferredListingEntry(result[dupIndex], hotel);
    const images = mergeHotelListingImages(result[dupIndex], hotel);
    result[dupIndex] = images.length
      ? { ...merged, images, Images: images }
      : merged;
  });
  return result;
};

/** Static Mongo row — metadata only; never overlay stale publish/min prices on live fare. */
export const stripStaticPriceFields = (staticHotel = {}) => {
  const {
    hotelMinPrice,
    HotelMinPrice,
    hotelPublishPrice,
    HotelPublishPrice,
    hotelNetPrice,
    HotelNetPrice,
    supplierGrossPrice,
    commission,
    markup,
    agentMarkup,
    ...meta
  } = staticHotel;
  return meta;
};

export { normalizeHotelListingName };
